/**
 * PIA/DPIA Generator Types
 * Privacy Impact Assessment / Data Protection Impact Assessment
 */

export type AssessmentType = 'pia' | 'dpia';

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export type ProcessingLawfulBasis =
  | 'consent'
  | 'contract'
  | 'legal_obligation'
  | 'vital_interests'
  | 'public_task'
  | 'legitimate_interests';

export type DataSubjectCategory =
  | 'customers'
  | 'employees'
  | 'contractors'
  | 'website_visitors'
  | 'children'
  | 'vulnerable_persons'
  | 'patients'
  | 'students'
  | 'general_public'
  | 'other';

export type PersonalDataType =
  | 'basic_identity'
  | 'contact_details'
  | 'financial'
  | 'location'
  | 'behavioral'
  | 'biometric'
  | 'health'
  | 'genetic'
  | 'racial_ethnic'
  | 'political_opinions'
  | 'religious_beliefs'
  | 'trade_union'
  | 'sexual_orientation'
  | 'criminal_data'
  | 'other';

export interface ProcessingPurpose {
  id: string;
  description: string;
  lawfulBasis: ProcessingLawfulBasis;
  lawfulBasisJustification?: string;
}

export interface IdentifiedRisk {
  id: string;
  description: string;
  likelihood: RiskLevel;
  impact: RiskLevel;
  overallRisk: RiskLevel;
  affectedRights: string[];
  mitigationMeasures: string[];
  residualRisk: RiskLevel;
  riskOwner?: string;
}

export interface DataFlow {
  id: string;
  source: string;
  destination: string;
  dataTypes: PersonalDataType[];
  transferMechanism?: string;
  safeguards?: string;
}

export interface Assessment {
  id: string;
  type: AssessmentType;
  name: string;
  projectDescription: string;
  status: 'draft' | 'in_review' | 'approved' | 'rejected';

  // Section 1: Processing Overview
  processingDescription: string;
  purposes: ProcessingPurpose[];
  dataSubjects: DataSubjectCategory[];
  estimatedSubjects?: string;
  personalDataTypes: PersonalDataType[];

  // Section 2: Data Flows
  dataFlows: DataFlow[];
  dataRetention?: string;
  thirdPartyProcessors: string[];
  internationalTransfers: boolean;
  transferCountries?: string[];

  // Section 3: Necessity & Proportionality
  necessityJustification: string;
  proportionalityJustification: string;
  alternativesConsidered?: string;
  dataMinimization?: string;

  // Section 4: Risks & Mitigations
  risks: IdentifiedRisk[];

  // Section 5: Consultation
  dpoConsulted: boolean;
  dpoOpinion?: string;
  dataSubjectsConsulted: boolean;
  consultationDetails?: string;
  supervisoryAuthorityConsultation: boolean;
  authorityDetails?: string;

  // Section 6: Review & Approval
  preparedBy: string;
  preparedDate: string;
  reviewedBy?: string;
  reviewDate?: string;
  approvedBy?: string;
  approvalDate?: string;
  nextReviewDate?: string;

  // Metadata
  created_at: string;
  updated_at: string;
}

// Configuration
export const ASSESSMENT_TYPE_CONFIG: Record<AssessmentType, {
  label: string;
  fullName: string;
  description: string;
}> = {
  pia: {
    label: 'PIA',
    fullName: 'Privacy Impact Assessment',
    description: 'General privacy impact assessment for any jurisdiction',
  },
  dpia: {
    label: 'DPIA',
    fullName: 'Data Protection Impact Assessment',
    description: 'GDPR-mandated assessment for high-risk processing',
  },
};

export const LAWFUL_BASIS_CONFIG: Record<ProcessingLawfulBasis, {
  label: string;
  description: string;
  gdprArticle: string;
}> = {
  consent: {
    label: 'Consent',
    description: 'Individual has given clear consent for processing',
    gdprArticle: 'Art. 6(1)(a)',
  },
  contract: {
    label: 'Contract',
    description: 'Processing necessary for contract performance',
    gdprArticle: 'Art. 6(1)(b)',
  },
  legal_obligation: {
    label: 'Legal Obligation',
    description: 'Processing necessary to comply with the law',
    gdprArticle: 'Art. 6(1)(c)',
  },
  vital_interests: {
    label: 'Vital Interests',
    description: 'Processing necessary to protect someone\'s life',
    gdprArticle: 'Art. 6(1)(d)',
  },
  public_task: {
    label: 'Public Task',
    description: 'Processing necessary for public interest tasks',
    gdprArticle: 'Art. 6(1)(e)',
  },
  legitimate_interests: {
    label: 'Legitimate Interests',
    description: 'Processing necessary for legitimate interests',
    gdprArticle: 'Art. 6(1)(f)',
  },
};

export const RISK_LEVEL_CONFIG: Record<RiskLevel, {
  label: string;
  color: string;
  description: string;
}> = {
  low: {
    label: 'Low',
    color: '#22c55e',
    description: 'Minimal risk to data subjects',
  },
  medium: {
    label: 'Medium',
    color: '#f59e0b',
    description: 'Moderate risk requiring standard controls',
  },
  high: {
    label: 'High',
    color: '#ef4444',
    description: 'Significant risk requiring enhanced controls',
  },
  critical: {
    label: 'Critical',
    color: '#991b1b',
    description: 'Severe risk that may require DPA consultation',
  },
};

export const DATA_SUBJECT_CONFIG: Record<DataSubjectCategory, {
  label: string;
  description: string;
}> = {
  customers: { label: 'Customers', description: 'Business customers or clients' },
  employees: { label: 'Employees', description: 'Staff members' },
  contractors: { label: 'Contractors', description: 'External contractors and consultants' },
  website_visitors: { label: 'Website Visitors', description: 'Website and app users' },
  children: { label: 'Children', description: 'Individuals under 18 (special category)' },
  vulnerable_persons: { label: 'Vulnerable Persons', description: 'Vulnerable individuals (special category)' },
  patients: { label: 'Patients', description: 'Healthcare patients' },
  students: { label: 'Students', description: 'Educational students' },
  general_public: { label: 'General Public', description: 'Members of the public' },
  other: { label: 'Other', description: 'Other data subject categories' },
};

export const PERSONAL_DATA_TYPE_CONFIG: Record<PersonalDataType, {
  label: string;
  description: string;
  isSpecialCategory: boolean;
}> = {
  basic_identity: { label: 'Basic Identity', description: 'Name, DOB, ID numbers', isSpecialCategory: false },
  contact_details: { label: 'Contact Details', description: 'Email, phone, address', isSpecialCategory: false },
  financial: { label: 'Financial Data', description: 'Bank accounts, income, credit', isSpecialCategory: false },
  location: { label: 'Location Data', description: 'GPS, IP addresses, travel data', isSpecialCategory: false },
  behavioral: { label: 'Behavioral Data', description: 'Browsing history, preferences', isSpecialCategory: false },
  biometric: { label: 'Biometric Data', description: 'Fingerprints, facial recognition', isSpecialCategory: true },
  health: { label: 'Health Data', description: 'Medical records, health status', isSpecialCategory: true },
  genetic: { label: 'Genetic Data', description: 'DNA, genetic characteristics', isSpecialCategory: true },
  racial_ethnic: { label: 'Racial/Ethnic Origin', description: 'Race or ethnic background', isSpecialCategory: true },
  political_opinions: { label: 'Political Opinions', description: 'Political views or affiliations', isSpecialCategory: true },
  religious_beliefs: { label: 'Religious Beliefs', description: 'Religion or philosophical beliefs', isSpecialCategory: true },
  trade_union: { label: 'Trade Union Membership', description: 'Union membership status', isSpecialCategory: true },
  sexual_orientation: { label: 'Sexual Orientation', description: 'Sexual orientation or sex life', isSpecialCategory: true },
  criminal_data: { label: 'Criminal Data', description: 'Criminal convictions or offenses', isSpecialCategory: true },
  other: { label: 'Other', description: 'Other personal data types', isSpecialCategory: false },
};

export function generateAssessmentId(): string {
  return `assess-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function generateRiskId(): string {
  return `risk-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function generatePurposeId(): string {
  return `purpose-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function generateDataFlowId(): string {
  return `flow-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function calculateOverallRisk(likelihood: RiskLevel, impact: RiskLevel): RiskLevel {
  const riskMatrix: Record<RiskLevel, Record<RiskLevel, RiskLevel>> = {
    low: { low: 'low', medium: 'low', high: 'medium', critical: 'medium' },
    medium: { low: 'low', medium: 'medium', high: 'high', critical: 'high' },
    high: { low: 'medium', medium: 'high', high: 'high', critical: 'critical' },
    critical: { low: 'medium', medium: 'high', high: 'critical', critical: 'critical' },
  };
  return riskMatrix[likelihood][impact];
}
